import { useTranslation } from 'react-i18next';
import GlowButton from '../../../components/GlowButton';

export default function BroadcastShowcase() {
  const { t } = useTranslation();
  
  return (
    <section className="py-24 px-6 bg-gray-950 relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-purple-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/30 rounded-full mb-6">
            <i className="ri-send-plane-fill text-purple-400"></i>
            <span className="text-purple-300 text-sm font-medium">{t('broadcast.badge')}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('broadcast.title')}
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            {t('broadcast.subtitle')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* Campaign Interface */}
          <div className="bg-gray-900/60 backdrop-blur-sm border border-gray-800 rounded-3xl overflow-hidden shadow-2xl">
            {/* Window Header */}
            <div className="bg-gray-800/80 px-6 py-4 flex items-center gap-3 border-b border-gray-700">
              <div className="flex gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
              </div>
              <span className="text-gray-300 text-sm font-medium">{t('broadcast.campaign.header')}</span>
            </div>

            {/* Campaign Content */}
            <div className="p-8 space-y-6">
              {/* Campaign Name */}
              <div>
                <label className="text-gray-400 text-sm mb-2 block">{t('broadcast.campaign.name')}</label>
                <div className="bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3">
                  <span className="text-white">{t('broadcast.campaign.nameValue')}</span>
                </div>
              </div>

              {/* Personalized Message */}
              <div>
                <label className="text-gray-400 text-sm mb-2 block">{t('broadcast.campaign.message')}</label>
                <div className="bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-4">
                  <p className="text-white leading-relaxed">
                    {t('broadcast.campaign.messageValue')}
                  </p>
                </div>
              </div>

              {/* Segmentation */}
              <div>
                <label className="text-gray-400 text-sm mb-3 block">{t('broadcast.campaign.segmentation')}</label>
                <div className="space-y-2">
                  <div className="flex items-center gap-3 bg-gray-800/30 border border-blue-500/30 rounded-lg px-4 py-3">
                    <div className="w-5 h-5 flex items-center justify-center bg-blue-500 rounded">
                      <i className="ri-check-line text-white text-xs"></i>
                    </div>
                    <span className="text-white text-sm">{t('broadcast.campaign.segment1')}</span>
                  </div>
                  <div className="flex items-center gap-3 bg-gray-800/30 border border-purple-500/30 rounded-lg px-4 py-3">
                    <div className="w-5 h-5 flex items-center justify-center bg-purple-500 rounded">
                      <i className="ri-check-line text-white text-xs"></i>
                    </div>
                    <span className="text-white text-sm">{t('broadcast.campaign.segment2')}</span>
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-gray-400 text-sm">{t('broadcast.campaign.progress')}</label>
                  <span className="text-white font-semibold text-sm">75%</span>
                </div>
                <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-purple-500 via-blue-500 to-cyan-500 rounded-full" style={{ width: '75%' }}></div>
                </div>
                <div className="flex items-center justify-between mt-3 text-xs">
                  <div className="flex items-center gap-1 text-purple-400">
                    <i className="ri-user-follow-line"></i>
                    <span>1,458 {t('broadcast.campaign.recipients')}</span>
                  </div>
                  <div className="flex items-center gap-1 text-green-400">
                    <i className="ri-mail-check-line"></i>
                    <span>1,094 {t('broadcast.campaign.delivered')}</span>
                  </div>
                  <div className="flex items-center gap-1 text-blue-400">
                    <i className="ri-eye-line"></i>
                    <span>873 {t('broadcast.campaign.read')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Features List */}
          <div className="space-y-6">
            <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-8 hover:border-purple-500/50 transition-all duration-300">
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl mb-6">
                <i className="ri-focus-3-fill text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">{t('broadcast.features.strategic.title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('broadcast.features.strategic.description')}
              </p>
            </div>

            <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-8 hover:border-blue-500/50 transition-all duration-300">
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl mb-6">
                <i className="ri-time-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">{t('broadcast.features.realtime.title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('broadcast.features.realtime.description')}
              </p>
            </div>

            <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-8 hover:border-green-500/50 transition-all duration-300">
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl mb-6">
                <i className="ri-settings-3-fill text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">{t('broadcast.features.automation.title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('broadcast.features.automation.description')}
              </p>
            </div>

            <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-8 hover:border-yellow-500/50 transition-all duration-300">
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl mb-6">
                <i className="ri-user-star-fill text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">{t('broadcast.features.personalization.title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('broadcast.features.personalization.description')}
              </p>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <div className="mt-16 text-center">
          <GlowButton variant="secondary" className="text-lg px-10 py-5">
            <i className="ri-send-plane-fill text-xl"></i>
            {t('broadcast.cta')}
          </GlowButton>
        </div>
      </div>
    </section>
  );
}
